import {
  SizeCharts_BodyMeasurementsTable,
  SizeCharts_SizeSystem,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_charts.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  DropDownProps,
  FONT_SIZES,
  FlyoutProps,
  Table,
  Text,
} from '@aboutyou/ui-components';
import {range} from '@aboutyou/utils';
import React, {FC} from 'react';
import styled from 'styled-components';
import {SizesSystemSelector} from './SizesSystemSelector';
import {useSelectedSizeSystem} from './useSelectedSizeSystem';

export const BodyMeasurementsChart: FC<{
  selectedOption: number;
  bodyMeasurementsTable: SizeCharts_BodyMeasurementsTable;
  sizeSystemSelector?: React.ReactNode;
  sizeSystemLayout: React.FC<FlyoutProps> | React.FC<DropDownProps>;
  setSelectedSizeSystemType: React.Dispatch<React.SetStateAction<number>>;
}> = ({
  selectedOption,
  bodyMeasurementsTable,
  sizeSystemLayout,
  setSelectedSizeSystemType,
}) => {
  const {t} = useTranslation();

  const selectedSizeSystem = useSelectedSizeSystem(
    selectedOption,
    bodyMeasurementsTable.sizeSystems,
  );

  const bodyMeasurementsTitles = bodyMeasurementsTable
    ? bodyMeasurementsTable.bodyMeasurements.map((measurements, index) => (
        <Text key={`body-measurement-${index}`} size="px10" weight="w500">
          {measurements.name}
        </Text>
      ))
    : [];

  return (
    <>
      <Text weight="w500" size="px16" data-testid="bodyMeasurementsChart-title">
        {t('Body Measurements')}
      </Text>
      <StyledTable
        headerContent={[
          [
            <>
              <SizesSystemSelector
                selectedOption={selectedOption}
                options={bodyMeasurementsTable?.sizeSystems}
                onClick={setSelectedSizeSystemType}
                LayoutComponent={sizeSystemLayout}
                dataTestId="bodyMeasurementsChartSelector"
                openedStateKey="size-chart-selector-2"
              />
            </>,
            ...bodyMeasurementsTitles,
          ],
        ]}
        bodyContent={normalizeTableBody(
          bodyMeasurementsTable,
          selectedSizeSystem,
        )}
        dataTestId="bodyMeasurementsChart-table"
      />
    </>
  );
};

const normalizeTableBody = (
  bodyMeasurementsTable: SizeCharts_BodyMeasurementsTable | undefined,
  selectedOption: SizeCharts_SizeSystem | undefined,
) => {
  if (!bodyMeasurementsTable || !selectedOption) return [];

  const sizeSystemsLength = selectedOption?.sizeLabels.length;
  return range(0, sizeSystemsLength).map(index => {
    const bodyMeasurementsContent = bodyMeasurementsTable.bodyMeasurements.map(
      measurements => [measurements.sizeLabels[index]],
    );
    return [selectedOption?.sizeLabels[index], ...bodyMeasurementsContent];
  });
};

const StyledTable = styled(Table)`
  margin-top: 10px;
  font-size: ${FONT_SIZES.px12};
`;
